import { Divider } from '@mui/material';
import { MemreFlexBox } from 'components/core';
import { Vocabulary } from 'services/useItem.service/interactions';
import { useAuthoringContext } from '../AuthoringContext';
import AttachInstructionalItem from '../components/AttachInstructionalItem';
import GraduatedDifficulty from '../components/GraduatedDifficulty';
import { IncorrectAnswers } from '../components/IncorrectAnswers';
import { AnswerField } from '../components/AnswerField';
import { TConcept, TSentence } from 'types';
import VocabularySentences from '../components/VocabularySentences';

export default function VocabularyEditor() {
  const { item, set: assignment } = useAuthoringContext();
  const vocabularyItem = item?.templateType === 'vocabulary' ? (item as Vocabulary) : null;
  const anchor: TConcept | null = vocabularyItem?.rawItem.facets[0]?.anchor;
  const association: TConcept | null = vocabularyItem?.rawItem.facets[0]?.association;

  if (!vocabularyItem || !anchor || !association) {
    return <div>Loading...</div>;
  }

  const rawItem = vocabularyItem.rawItem;
  const sentences = rawItem.sentences || [];

  const handleSentencesChange = (updatedSentences: TSentence[]) => {
    vocabularyItem.updateRawItem({
      ...rawItem,
      sentences: updatedSentences,
    });
  };

  return (
    <MemreFlexBox
      direction="column"
      gap={2}
      sx={{
        p: 2,
        pl: 0,
        width: '100%',
      }}
    >
      {/* Foreign Term */}
      <MemreFlexBox direction="column" gap={2} sx={{ width: '100%' }}>
        <h3>Foreign Term</h3>
        <AnswerField
          concept={anchor}
          label="Foreign Term"
          updateAnswerLocally={vocabularyItem.updateConceptLocally}
          backgroundColor={'white'}
          allowSound={true}
        />
        <IncorrectAnswers
          conceptIdForDistractors={anchor.id}
          title="Wrong Answer Options for Foreign Term"
          questionForSuggestedDistractors={`What is the foreign term for '${anchor.text}'?`}
          correctAnswersForSuggestedDistractors={anchor.text ? [anchor.text] : []}
        />
      </MemreFlexBox>

      <Divider sx={{ width: '100%', m: 0.5 }} />

      {/* Native Term */}
      <MemreFlexBox direction="column" gap={2} sx={{ width: '100%' }}>
        <h3>Native Term</h3>
        <AnswerField
          concept={association}
          label="Native Term"
          updateAnswerLocally={vocabularyItem.updateConceptLocally}
          backgroundColor={'white'}
          allowSound={true}
        />
        <IncorrectAnswers
          conceptIdForDistractors={association.id}
          title="Wrong Answer Options for Native Term"
          questionForSuggestedDistractors={`What is the native term for '${association.text}'?`}
          correctAnswersForSuggestedDistractors={association.text ? [association.text] : []}
        />
      </MemreFlexBox>

      <Divider sx={{ width: '100%', m: 0.5 }} />

      {/* Sentences Component - only show when both terms have text */}
      {anchor.text && association.text && (
        <>
          <VocabularySentences
            itemId={vocabularyItem.id}
            assignmentId={assignment?.id || ''}
            term={anchor.text}
            sentences={sentences}
            onSentencesChange={handleSentencesChange}
          />
          <Divider sx={{ width: '100%', m: 0.5, mt: 2 }} />
        </>
      )}

      {/* Instructional Content */}
      {vocabularyItem?.id && <AttachInstructionalItem itemId={vocabularyItem.id} />}

      <Divider sx={{ width: '100%', m: 0.5, mt: 2 }} />

      {/* Graduated Difficulty */}
      {vocabularyItem?.id && <GraduatedDifficulty itemId={vocabularyItem.id} />}
    </MemreFlexBox>
  );
}
